import { useState, useCallback } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { roundAmount2Dig } from "utils/amount";
import {
  exclusiveToInclusive,
  withSyncedRatesFromExclusive,
  withSyncedRatesFromInclusive,
} from "utils/salebillRate";
import ItemSelectSizeGrouped from "components/item/ItemSelectSizeGrouped";
import { useItemDetailsWithSizes } from "http-hooks/agitem/useAgitem";
import { useAllSalebills, useCndnItemRowDetails } from "http-hooks/salebill/useSalebill";
import { useBill } from "hooks/bill";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  ActionButton,
} from "components/ui/Table";

export default function CndnTable() {
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [itemSizes, setItemSizes] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    selectBoxes,
    tableData,
    setTableData,
    removeTableItemsRow,
    companyHasGst,
    isForeignCurrencyBill,
  } = useBill();

  const vendid = selectBoxes.vendor?.ps_Id;
  const cusid = selectBoxes.customer?.ps_Id;

  const { data: cndnBillsData } = useAllSalebills(
    {
      vendid: vendid || "",
      cusid: cusid || "",
      billtype: "DIR,STK,PL,ORD",
      noPagination: "true",
    },
    { enabled: !!(vendid || cusid) }
  );
  const cndnBills = cndnBillsData?.rows || [];

  const { mutateAsync: fetchItemSizes } = useItemDetailsWithSizes();
  const { mutateAsync: fetchCndnItemDetails } = useCndnItemRowDetails();

  const handleSelectedItem = useCallback(async (val) => {
    if (val) {
      setSelectedItem(val);
      const sizes = await fetchItemSizes(val.value);
      const sizesWithQty = (sizes || []).map((s) => ({ ...s, qty: 0 }));
      setItemSizes(sizesWithQty);
    } else {
      setSelectedItem(null);
      setItemSizes([]);
    }
  }, [fetchItemSizes]);

  const handleItemRowSizeQtyChange = (index, val) => {
    setItemSizes((prev) => [
      ...prev.slice(0, index),
      { ...prev[index], qty: parseFloat(val) || 0 },
      ...prev.slice(index + 1),
    ]);
  };

  const handleUpdatesItemSizes = async () => {
    try {
      setIsUpdating(true);

      const sizesWithQty = itemSizes.filter((sz) => sz.qty > 0);
      if (sizesWithQty.length === 0) return;

      const mappedData = [];
      for (const sizeInfo of sizesWithQty) {
        const details = await fetchCndnItemDetails({
          itemid: sizeInfo.agitemdtlId,
          billid: selectedBill?.salebillid || "",
          vendid: vendid || "",
        });

        mappedData.push({
          selected: true,
          ordno: "",
          caseno: 0,
          slotqty: 1,
          sqty: sizeInfo.qty,
          srate: details?.rate || 0,
          srate_incl: companyHasGst && !isForeignCurrencyBill
            ? exclusiveToInclusive(details?.rate || 0, details?.itemtaxrate || 0)
            : details?.rate || 0,
          dis1: 0,
          tax: isForeignCurrencyBill ? 0 : companyHasGst ? details?.itemtaxrate || 0 : 0,
          slbno: selectedBill?.salebillid || "",
          item: {
            itemid: sizeInfo.agitemdtlId,
            item_full_name: selectedItem?.label || "",
            aguomconval: details?.conval || selectedItem?.conval || 1,
            itmhsnId: details?.hsnid || selectedItem?.itmhsnId || "",
            itemuom: details?.uom || selectedItem?.itemuom || "",
          },
          source: "CNDN",
          isAdditionalItems: false,
        });
      }

      setTableData([...tableData, ...mappedData]);
      setSelectedItem(null);
      setItemSizes([]);
    } catch (error) {
      console.error("Error updating item sizes:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRateChange = useCallback((itemRowIndex, newRate) => {
    const newData = tableData.map((r, i) =>
      i === itemRowIndex
        ? withSyncedRatesFromExclusive(r, parseFloat(newRate) || 0)
        : r
    );
    setTableData(newData);
  }, [tableData, setTableData]);

  const handleRateInclChange = useCallback((itemRowIndex, newRateIncl) => {
    const newData = tableData.map((r, i) =>
      i === itemRowIndex
        ? withSyncedRatesFromInclusive(r, parseFloat(newRateIncl) || 0)
        : r
    );
    setTableData(newData);
  }, [tableData, setTableData]);

  const handleQtyChange = useCallback((itemRowIndex, newQty) => {
    const newData = tableData.map((r, i) =>
      i === itemRowIndex ? { ...r, sqty: parseFloat(newQty) || 0 } : r
    );
    setTableData(newData);
  }, [tableData, setTableData]);

  const handleHsnChange = useCallback(async (itemRowIndex, newHsn) => {
    const row = tableData[itemRowIndex];
    const details = await fetchCndnItemDetails({
      itemid: row.item.itemid,
      billid: "",
      vendid: vendid || "",
    });

    const newData = tableData.map((r, i) =>
      i === itemRowIndex
        ? {
            ...r,
            item: { ...r.item, itmhsnId: newHsn },
            tax: companyHasGst ? details?.itemtaxrate || r.tax : 0,
          }
        : r
    );
    setTableData(newData);
  }, [companyHasGst, tableData, setTableData, fetchCndnItemDetails, vendid]);

  return (
    <div className="w-full flex gap-2 min-h-[500px] min-w-[1200px]">
      {/* Left panel - Item selector */}
      <div className="w-1/3">
        <Table>
          <THead>
            <TR>
              <TH compact first last>Add Item</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD compact first last className="align-top space-y-3 p-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Bill No. (optional)
                  </label>
                  <Select
                    value={selectedBill}
                    onChange={setSelectedBill}
                    options={cndnBills}
                    getOptionValue={(o) => o.salebillid}
                    getOptionLabel={(o) => o.salebillno}
                    placeholder="Select bill..."
                    isClearable={true}
                    showLabel={false}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Item
                  </label>
                  <ItemSelectSizeGrouped
                    value={selectedItem}
                    onChange={handleSelectedItem}
                    vendorId={vendid}
                    placeholder="Select item..."
                    isDisabled={!vendid}
                  />
                </div>
                {selectedItem && itemSizes.length > 0 ? (
                  <Table className="mt-2">
                    <THead>
                      <TR>
                        <TH compact first colSpan={2}>Item Sizes</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {itemSizes.map((itemSize, rowindex) => (
                        <TR key={`${rowindex}-${itemSize.agitemdtlId}`}>
                          <TD compact first className="border-r border-gray-200">
                            <div className="flex flex-col">
                              <span className="font-medium text-xs">
                                {itemSize.sz} {itemSize.szuom}
                              </span>
                              <span
                                className={`text-[10px] ${
                                  itemSize.packratio === 0 ? "text-red-500" : "text-gray-500"
                                }`}
                              >
                                PR: {itemSize.packratio || 0}
                              </span>
                            </div>
                          </TD>
                          <TD compact last className="border-r border-gray-200">
                            <Input
                              type="number"
                              step="0.001"
                              value={itemSize.qty}
                              onChange={(e) =>
                                handleItemRowSizeQtyChange(rowindex, e.target.value)
                              }
                              showLabel={false}
                              className="w-full [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-right"
                            />
                          </TD>
                        </TR>
                      ))}
                      <TR>
                        <TD compact first colSpan={2}>
                          <button
                            type="button"
                            onClick={handleUpdatesItemSizes}
                            disabled={isUpdating}
                            className="rounded bg-gray-700 px-2.5 py-1 text-xs font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isUpdating ? "Adding..." : "Add"}
                          </button>
                        </TD>
                      </TR>
                    </TBody>
                  </Table>
                ) : null}
              </TD>
            </TR>
          </TBody>
        </Table>
      </div>

      {/* Right panel - Data table */}
      <div className="w-2/3 overflow-x-auto">
        <Table>
          <THead>
            <TR>
              <TH compact first className="w-[120px]">Bill No.</TH>
              <TH compact>Item Name</TH>
              <TH compact className="w-[70px] text-right">Rate</TH>
              {companyHasGst && <TH compact className="w-[80px] text-right" title="Rate including tax">
                Rate Incl
              </TH>}
              <TH compact className="w-[65px] text-right">Qty</TH>
              <TH compact className="w-[40px] text-center">UOM</TH>
              <TH compact className="w-[80px] text-right">Amount</TH>
              <TH compact className="w-[60px] text-center">HSN</TH>
              {companyHasGst && <TH compact className="w-[50px] text-right">Tax%</TH>}
              <TH compact last className="w-[40px] text-center"></TH>
            </TR>
          </THead>
          <TBody>
            {tableData?.map((row, itemRowIndex) => {
              if (!row.selected) return null;
              const billLabel = cndnBills?.find(
                (b) => b.salebillid === row.slbno
              )?.salebillno || row.billNoDisplay || "";

              return (
                <TR key={`cndn-${itemRowIndex}-${row.item.itemid}`}>
                  <TD compact first className="border-r border-gray-200 text-xs">
                    {billLabel}
                  </TD>
                  <TD compact className="border-r border-gray-200 text-xs">
                    {row.item.item_full_name}
                  </TD>
                  <TD compact className="border-r border-gray-200">
                    <Input
                      type="number"
                      step="0.01"
                      value={row.srate || 0}
                      onChange={(e) => handleRateChange(itemRowIndex, e.target.value)}
                      showLabel={false}
                      className="w-[60px] [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-right"
                    />
                  </TD>
                  {companyHasGst && <TD compact className="border-r border-gray-200">
                    <Input
                      type="number"
                      step="0.01"
                      value={row.srate_incl ?? exclusiveToInclusive(row.srate, row.tax)}
                      onChange={(e) => handleRateInclChange(itemRowIndex, e.target.value)}
                      showLabel={false}
                      className="w-[70px] [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-right"
                    />
                  </TD>}
                  <TD compact className="border-r border-gray-200">
                    <Input
                      type="number"
                      step="0.00001"
                      value={row.sqty || 0}
                      onChange={(e) => handleQtyChange(itemRowIndex, e.target.value)}
                      showLabel={false}
                      className="w-[55px] [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-right"
                    />
                  </TD>
                  <TD compact className="border-r border-gray-200 text-center text-xs">
                    {row.item.itemuom}
                  </TD>
                  <TD compact className="border-r border-gray-200 text-right text-xs">
                    {roundAmount2Dig(
                      (parseFloat(row.srate) || 0) * (parseFloat(row.sqty) || 0)
                    )}
                  </TD>
                  <TD compact className="border-r border-gray-200">
                    <Input
                      type="text"
                      value={row.item.itmhsnId || ""}
                      onChange={(e) => handleHsnChange(itemRowIndex, e.target.value)}
                      showLabel={false}
                      className="w-[50px] [&_input]:py-0.5 [&_input]:px-1 [&_input]:text-sm [&_input]:text-center"
                    />
                  </TD>
                  {companyHasGst && <TD compact className="text-right text-xs">
                    {row.tax || 0}%
                  </TD>}
                  <TD compact last className="text-center">
                    <ActionButton
                      onClick={() => removeTableItemsRow(itemRowIndex)}
                      variant="danger"
                      title="Remove row"
                      className="mr-0 !px-1.5 !py-1"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </ActionButton>
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </div>
    </div>
  );
}
